import {
  EMPTY,
  formatDate,
  formatHours,
  isTaskOverdue,
  routes,
  type Task,
  type TaskSummary,
  todayIso,
} from '@pmai/contracts';
import { type Column, ConfirmDialog, DataTable, IconButton, StatusBadge } from '@pmai/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiMutation } from '../../shared/api/hooks';
import { usePermissions } from '../../shared/auth/session';
import { useConfirm } from '../../shared/use-confirm';
import { TASK_INVALIDATES, TaskDialog, type TaskScope } from './task-dialog';

export type TaskColumn =
  'check' | 'project' | 'title' | 'assignee' | 'plan' | 'fact' | 'deadline' | 'done' | 'actions';

/** "Kopā: N · Pabeigti: x/N · Plānotais: Σ h · Faktiskais: Σ h" (FR-TASK-003, FR-CUST-013). */
export function TaskSummaryLine({ summary }: { summary: TaskSummary }) {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-ink-muted" data-testid="task-summary">
      {t('tasks.summary', {
        count: summary.count,
        done: summary.done,
        planned: formatHours(summary.estimatedHours),
        actual: formatHours(summary.actualHours),
      })}
    </p>
  );
}

/**
 * Every task table (project card, customer card, tasks page): the views choose columns; done
 * toggling, editing and deleting work the same everywhere (FR-TASK-005…008, FR-CUST-016).
 * Overdue open tasks show the deadline in red, done ones are muted, priority ones have 🔥.
 */
export function TasksTable({
  label,
  rows,
  loading,
  columns,
  scope,
  empty,
  totals,
}: {
  label: string;
  rows: readonly Task[] | undefined;
  loading: boolean;
  columns: readonly TaskColumn[];
  /** Which projects the edit form offers. */
  scope: TaskScope;
  empty: ReactNode;
  /** Totals in the footer (FR-TASK-010) — Σ plan, Σ fact and the count. */
  totals?: TaskSummary;
}) {
  const { t } = useTranslation();
  const canEdit = usePermissions().includes('tasks.edit');
  const [editing, setEditing] = useState<Task | null>(null);
  const setDone = useApiMutation(routes.tasks.setDone, { invalidates: TASK_INVALIDATES });
  const remove = useConfirm(routes.tasks.remove, {
    invalidates: TASK_INVALIDATES,
    params: (task: Task) => ({ id: task.id }),
  });
  const today = todayIso();
  const f = (field: string) => t(`tasks.fields.${field}`);
  const hours = (value: number | null) => (value ? formatHours(value) : EMPTY);

  const all: Record<TaskColumn, Column<Task>> = {
    check: {
      key: 'check',
      header: '',
      className: 'w-10',
      cell: (task) => (
        <IconButton
          icon={task.done ? 'square-check' : 'square'}
          label={t(task.done ? 'tasks.markOpen' : 'tasks.markDone', { title: task.title })}
          aria-pressed={task.done}
          disabled={!canEdit}
          onClick={() => setDone.mutate({ params: { id: task.id }, body: { done: !task.done } })}
        />
      ),
    },
    project: {
      key: 'project',
      header: f('project'),
      footer: totals && t('common.total'),
      cell: (task) => (
        <Link to={`/projects/${task.projectId}`} className="text-brand-700 hover:underline">
          {task.projectName}
        </Link>
      ),
    },
    title: {
      key: 'title',
      header: f('titleShort'),
      cell: (task) => (
        <span className={task.done ? 'text-ink-muted line-through' : undefined}>
          {task.isPriority && '🔥 '}
          {task.title}
        </span>
      ),
    },
    assignee: { key: 'assignee', header: f('assignee'), cell: (task) => task.assigneeName ?? EMPTY },
    plan: {
      key: 'plan',
      header: f('plan'),
      align: 'right',
      footer: totals && formatHours(totals.estimatedHours),
      cell: (task) => hours(task.estimatedHours),
    },
    fact: {
      key: 'fact',
      header: f('fact'),
      align: 'right',
      footer: totals && formatHours(totals.actualHours),
      cell: (task) => hours(task.actualHours),
    },
    deadline: {
      key: 'deadline',
      header: f('deadline'),
      cell: (task) =>
        isTaskOverdue(task, today) ? (
          <span className="font-semibold text-red-600" title={t('tasks.overdue')}>
            {formatDate(task.deadline)}
            <span className="sr-only"> — {t('tasks.overdue')}</span>
          </span>
        ) : (
          formatDate(task.deadline)
        ),
    },
    done: {
      key: 'done',
      header: f('doneShort'),
      cell: (task) => (
        <StatusBadge
          tone={task.done ? 'success' : 'neutral'}
          label={t(task.done ? 'common.yes' : 'common.no')}
        />
      ),
    },
    actions: {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      footer: totals && t('tasks.count', { count: totals.count }),
      cell: (task) =>
        canEdit && (
          <div className="flex justify-end gap-1">
            <IconButton
              icon="pencil"
              label={`${t('common.edit')}: ${task.title}`}
              onClick={() => setEditing(task)}
            />
            <IconButton
              icon="trash-2"
              label={`${t('common.delete')}: ${task.title}`}
              onClick={() => remove.ask(task)}
            />
          </div>
        ),
    },
  };

  return (
    <>
      <DataTable
        label={label}
        rows={rows}
        rowKey={(task) => task.id}
        loading={loading}
        empty={empty}
        columns={columns.map((c) => all[c])}
        rowClassName={(task) => (task.done ? 'bg-canvas/60' : task.isPriority ? 'bg-amber-50/60' : undefined)}
      />
      <TaskDialog open={editing !== null} task={editing} scope={scope} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...remove.dialog}
        title={t('tasks.deleteTitle')}
        message={t('tasks.deleteText', { title: remove.target?.title ?? '' })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
